import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHeaderResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { map, tap } from 'rxjs';
import { isCockpitApiRequest } from '../auth/api-origin.utils';
import { ACCESS_CONTEXT_PATH, problemCodeOf } from '../../api/services/access-api.service';
import { ApplicationAccessStore } from '../../shared/store/application-access.store';

export const ACCESS_VERSION_HEADER = 'X-Cockpit-Access-Version';

/** Problem code of a response dropped because the access context changed while it was in flight. */
export const ACCESS_CONTEXT_CHANGED = 'ACCESS_CONTEXT_CHANGED';

/** Set on requests that must not depend on application access, such as K01 feedback submission. */
export const SKIP_ACCESS_REFRESH = new HttpContextToken<boolean>(() => false);

const isAccessContextRequest = (url: string): boolean => {
  try {
    return new URL(url, 'http://relative').pathname.endsWith(ACCESS_CONTEXT_PATH);
  } catch {
    return false;
  }
};

/** A response the caller never receives; status 0 so a write is treated as an unknown outcome. */
export const isAccessContextChangedError = (error: unknown): boolean =>
  error instanceof HttpErrorResponse && problemCodeOf(error) === ACCESS_CONTEXT_CHANGED;

/**
 * Re-reads application access when a Cockpit API response shows a different context version, a
 * denial (403) or unavailable access, and drops responses whose request started under an older
 * user or access generation, so stale protected data never reaches a reset page.
 */
export const applicationAccessInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isCockpitApiRequest(req.url) || isAccessContextRequest(req.url) || req.context.get(SKIP_ACCESS_REFRESH)) {
    return next(req);
  }

  const store = inject(ApplicationAccessStore);
  const generation = store.generation();

  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse || event instanceof HttpHeaderResponse) {
          store.observeVersion(event.headers.get(ACCESS_VERSION_HEADER));
        }
      },
      error: (error: unknown) => {
        if (!(error instanceof HttpErrorResponse)) {
          return;
        }

        if (error.status === 403 || problemCodeOf(error) === 'ACCESS_UNAVAILABLE') {
          store.refresh();
        } else {
          store.observeVersion(error.headers?.get(ACCESS_VERSION_HEADER) ?? null);
        }
      },
    }),
    map((event: HttpEvent<unknown>) => {
      if (event instanceof HttpResponse && store.generation() !== generation) {
        throw new HttpErrorResponse({
          status: 0,
          statusText: 'Application access changed',
          url: req.url,
          error: { code: ACCESS_CONTEXT_CHANGED },
        });
      }
      return event;
    })
  );
};
